import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  checkVoucherValidity,
  getDownloads,
  redeemB2BVoucher,
  redeemVoucher,
  removeMultiDownloads,
  setAutoPlay,
  setNextTitle,
} from "./thunks";
import { SettingState } from "./types";

export const name = "settings";

const initialState: SettingState = {
  voucherValidity: {
    data: false,
    status: "idle",
  },
  voucher: {
    data: false,
    status: "idle",
  },
  b2bVoucher: {
    data: false,
    status: "idle",
  },
  autoPlay: {
    status: "idle",
  },
  nextTitle: {
    status: "idle",
  },
  downloads: {
    data: null,
    status: "idle",
  },
  removeMultiDownloads: {
    status: "idle",
  },
};

const asyncActions = {
  checkVoucherValidityAsync: createAsyncThunk(
    `${name}/voucher/check-validity`,
    checkVoucherValidity
  ),
  redeemVoucherAsync: createAsyncThunk(`${name}/voucher/redeem`, redeemVoucher),
  redeemb2bVoucherAsync: createAsyncThunk(
    `${name}/b2b/voucher/redeem`,
    redeemB2BVoucher
  ),
  setAutoPlayAsync: createAsyncThunk(`${name}/account/autoplay`, setAutoPlay),
  setNextTitleAsync: createAsyncThunk(
    `${name}/account/next-title`,
    setNextTitle
  ),
  getDownloadsAsync: createAsyncThunk(
    `${name}/downloads/get/ids`,
    getDownloads
  ),
  removeDownloadsAsync: createAsyncThunk(
    `${name}/downloads/remove-ids`,
    removeMultiDownloads
  ),
};

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: (_) => {
      return initialState;
    },
    resetSuccessAlert: (state) => {
      state.removeMultiDownloads.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.checkVoucherValidityAsync.pending, (state) => {
        state.voucherValidity.status = "loading";
      })
      .addCase(
        asyncActions.checkVoucherValidityAsync.fulfilled,
        (state, action) => {
          state.voucherValidity.data = action.payload;
          state.voucherValidity.status = "success";
        }
      )
      .addCase(asyncActions.checkVoucherValidityAsync.rejected, (state) => {
        state.voucherValidity.status = "failed";
      })
      .addCase(asyncActions.redeemVoucherAsync.pending, (state) => {
        state.voucher.status = "loading";
      })
      .addCase(asyncActions.redeemVoucherAsync.fulfilled, (state, action) => {
        state.voucher.data = action.payload;
        state.voucher.status = "success";
      })
      .addCase(asyncActions.redeemVoucherAsync.rejected, (state) => {
        state.voucher.status = "failed";
      })
      .addCase(asyncActions.redeemb2bVoucherAsync.pending, (state) => {
        state.b2bVoucher.status = "loading";
      })
      .addCase(
        asyncActions.redeemb2bVoucherAsync.fulfilled,
        (state, action) => {
          state.b2bVoucher.data = action.payload;
          state.b2bVoucher.status = "success";
        }
      )
      .addCase(asyncActions.redeemb2bVoucherAsync.rejected, (state) => {
        state.b2bVoucher.status = "failed";
      })
      .addCase(asyncActions.setAutoPlayAsync.pending, (state) => {
        state.autoPlay.status = "loading";
      })
      .addCase(asyncActions.setAutoPlayAsync.fulfilled, (state) => {
        state.autoPlay.status = "success";
      })
      .addCase(asyncActions.setAutoPlayAsync.rejected, (state) => {
        state.autoPlay.status = "failed";
      })
      .addCase(asyncActions.setNextTitleAsync.pending, (state) => {
        state.nextTitle.status = "loading";
      })
      .addCase(asyncActions.setNextTitleAsync.fulfilled, (state) => {
        state.nextTitle.status = "success";
      })
      .addCase(asyncActions.setNextTitleAsync.rejected, (state) => {
        state.nextTitle.status = "failed";
      })
      .addCase(asyncActions.getDownloadsAsync.pending, (state) => {
        state.downloads.status = "loading";
      })
      .addCase(asyncActions.getDownloadsAsync.fulfilled, (state, action) => {
        state.downloads.data = Array.isArray(action.payload)
          ? action.payload
          : [action.payload];
        state.downloads.status = "success";
      })
      .addCase(asyncActions.getDownloadsAsync.rejected, (state) => {
        state.downloads.status = "failed";
      })
      .addCase(asyncActions.removeDownloadsAsync.pending, (state) => {
        state.removeMultiDownloads.status = "loading";
      })
      .addCase(asyncActions.removeDownloadsAsync.fulfilled, (state, action) => {
        state.removeMultiDownloads.status = "success";
      })
      .addCase(asyncActions.removeDownloadsAsync.rejected, (state) => {
        state.removeMultiDownloads.status = "failed";
      });
  },
});

export const SettingActions = {
  ...slice.actions,
  ...asyncActions,
};

export default slice.reducer;
